import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, PRIMARY_OUTLET, RouteReuseStrategy } from '@angular/router';
import { TabReuseCacheService } from './tab-reuse-cache.service';

@Injectable({ providedIn: 'root' })
export class TabRouteReuseStrategy implements RouteReuseStrategy {
  constructor(private readonly cache: TabReuseCacheService) {}

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (route.outlet !== PRIMARY_OUTLET || !route.routeConfig?.component || route.firstChild) {
      return false;
    }

    if (this.isTabReuseDisabled(route)) {
      return false;
    }

    return true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) {
      return;
    }

    const routeKey = this.getRouteKey(route);
    if (!this.cache.hasOpenTab(routeKey)) {
      this.cache.destroyDetachedHandle(handle);
      return;
    }

    this.cache.set(routeKey, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const routeKey = this.getRouteKey(route);
    if (this.isTabReuseDisabled(route)) {
      this.cache.delete(routeKey);
      return false;
    }

    return this.cache.hasCachedHandle(routeKey);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.cache.get(this.getRouteKey(route));
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === current.routeConfig;
  }

  private isTabReuseDisabled(route: ActivatedRouteSnapshot): boolean {
    if (route.routeConfig?.path === 'login' || route.data['disableTabReuse']) {
      return true;
    }

    const path = route.routeConfig?.path ?? '';
    // PrimeNG Tabs and other portal-heavy payment screens break detached route handles.
    return path.startsWith('payments/');
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const segments = route.pathFromRoot
      .flatMap(snapshot => snapshot.url.map(url => url.path))
      .filter(Boolean);
    return `/${segments.join('/')}`;
  }
}
